import { api } from '../lib/api';
import { useAsync } from '../lib/session';
import { short, monthLabel } from '../lib/fmt';
import { RankedBars } from '../components/charts';
import { Card, Note, PageHead, Tile, Tiles, Table, Th, Td, TableWrap, Tag } from '../components/ui';
import { Loading, HeadSkeleton, TilesSkeleton, CardSkeleton, TableSkeleton, BarsSkeleton } from '../components/skeleton';
import type { AccountBalance } from '../lib/types';

interface Row {
  month: string; opening: number; collections: number; retainers: number; inflow: number;
  payroll: number; bills: number; approved_claims: number; outflow: number;
  net: number; closing: number; is_current: boolean;
}
interface Feed {
  rows: Row[]; opening_cash: number; accounts: AccountBalance[];
  behaviour: { days_late: number; basis: string; samples: number };
  monthly_burn: number; wage_bill: number;
  runway_months: number | null; first_negative_month: string | null;
}

/** Head, the four figures, the month-by-month grid, then the accounts. */
function ProjectionSkeleton() {
  return (
    <Loading label="Loading the cash projection">
      <HeadSkeleton />
      <TilesSkeleton />
      <CardSkeleton><TableSkeleton rows={6} cols={6} cards={false} /></CardSkeleton>
      <CardSkeleton className="mt-5" sub={false}><BarsSkeleton rows={4} /></CardSkeleton>
    </Loading>
  );
}

export default function Projection() {
  const { data, loading, error } = useAsync<Feed>(
    () => api.get('/api/reports/projection', { horizon: 6 }), []);

  if (loading) return <ProjectionSkeleton />;
  if (error) return <Note title="That did not load" tone="critical">{error}</Note>;
  const p = data!;
  const neg = p.first_negative_month;

  return (
    <div>
      <PageHead eyebrow="Six months ahead" title="Cash projection"
        dek="What is contracted, what is owed to us, and what we must pay. Every figure comes from a record already in the books — nothing here is a guess dressed up as a forecast." />

      <Tiles>
        <Tile hero label="Cash on hand" value={short(p.opening_cash)}
              sub={<span className="text-muted">across {p.accounts.length} accounts</span>} />
        <Tile label="Monthly burn" value={short(p.monthly_burn)}
              sub={<span className="text-muted">wages {short(p.wage_bill)} plus recurring bills</span>} />
        <Tile label="Runway"
              value={p.runway_months === null ? '—' : p.runway_months < 0 ? 'Overdrawn' : `${p.runway_months} mo`}
              sub={<span className="text-muted">if nothing more were collected</span>} />
        <Tile label="First shortfall" value={neg ? monthLabel(neg) : 'None in 6 months'}
              sub={<span className="text-muted">{neg ? 'closing balance goes below zero' : 'stays positive throughout'}</span>} />
      </Tiles>

      {neg && (
        <Note title="Projected shortfall" tone="critical">
          On these assumptions the closing balance falls below zero in <b>{monthLabel(neg)}</b>.
          Either a collection has to land earlier or an outflow has to move.
        </Note>
      )}

      <Card title="Month by month"
            sub={<>Collections are timed from each invoice due date plus <b>{p.behaviour.days_late} days</b> — {p.behaviour.basis}
                 {p.behaviour.samples > 0 && `, from ${p.behaviour.samples} settled invoices`}.</>}
            flush>
        <TableWrap>
          <Table>
            <thead><tr>
              <Th>Month</Th><Th n>Opening</Th><Th n>Collections</Th><Th n>Retainers</Th>
              <Th n>Payroll</Th><Th n>Bills</Th><Th n>Claims</Th><Th n>Net</Th><Th n>Closing</Th>
            </tr></thead>
            <tbody>
              {p.rows.map((r) => (
                <tr key={r.month} className={r.is_current ? 'bg-card-2' : ''}>
                  <Td><b>{monthLabel(r.month)}</b>{r.is_current && <> <Tag>now</Tag></>}</Td>
                  <Td n>{short(r.opening)}</Td>
                  <Td n className="text-good">{r.collections ? short(r.collections) : '—'}</Td>
                  <Td n className="text-good">{r.retainers ? short(r.retainers) : '—'}</Td>
                  <Td n>{r.payroll ? short(r.payroll) : '—'}</Td>
                  <Td n>{r.bills ? short(r.bills) : '—'}</Td>
                  <Td n>{r.approved_claims ? short(r.approved_claims) : '—'}</Td>
                  <Td n className={r.net < 0 ? 'text-critical font-semibold' : 'text-good font-semibold'}>{short(r.net)}</Td>
                  <Td n className={r.closing < 0 ? 'text-critical font-semibold' : 'font-semibold'}>{short(r.closing)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </Card>

      <Card title="Where the cash sits today" className="mt-5">
        <RankedBars labelWidth={190}
          rows={p.accounts.map((a) => ({ name: a.name, amount: Math.max(a.balance, 0) }))} />
      </Card>

      <Note title="What this assumes">
        Every open invoice is eventually collected; active retainers keep billing on their current
        schedule; the wage bill and recurring bills stay at today's amounts. It does not model new
        business, tax payments, or capital spend. Treat the shape as reliable and the last decimal
        as not.
      </Note>
    </div>
  );
}
